import { useState } from "react";
import { Mail, MapPin, Clock, BadgeCheck, Phone, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { BookingForm } from "@/components/BookingForm";
import { api } from "@/lib/api";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF, BBB_URL, SERVICE_AREA, VISIT_TIMES } from "@/lib/images";

const CONTACT_CARDS = [
  { icon: Phone, label: "Call Us", value: PHONE, href: PHONE_HREF, testid: "contact-card-phone" },
  { icon: Mail, label: "Email Us", value: EMAIL, href: EMAIL_HREF, testid: "contact-card-email" },
  { icon: MapPin, label: "Service Area", value: SERVICE_AREA, testid: "contact-card-area" },
  { icon: Clock, label: "Visit Times", value: VISIT_TIMES, testid: "contact-card-times" },
  {
    icon: BadgeCheck,
    label: "Reviews & Trust",
    value: "See our BBB Business Profile",
    href: BBB_URL,
    external: true,
    testid: "contact-card-bbb",
  },
  { icon: Send, label: "We Reply", value: "Within one business day", testid: "contact-card-reply" },
];

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", senior_location: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        senior_location: form.senior_location || undefined,
        message: form.message,
      });
      setSent(true);
      toast.success("Message sent — we'll be in touch soon");
    } catch {
      toast.error(`Something went wrong — please email us directly at ${EMAIL}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-form-confirmation" className="rounded-3xl border border-sage/30 bg-sage-soft p-10 text-center">
        <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-sage" />
        <h3 className="mb-3 font-serif text-2xl font-semibold text-espresso">Message received!</h3>
        <p className="text-lg text-espresso/80">
          Thank you for reaching out. We typically reply within one business day.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="input-label">Your name *</label>
          <input id="contact-name" data-testid="contact-form-name-input" className="input-field" value={form.name} onChange={set("name")} required />
        </div>
        <div>
          <label htmlFor="contact-phone" className="input-label">Phone</label>
          <input id="contact-phone" data-testid="contact-form-phone-input" type="tel" className="input-field" value={form.phone} onChange={set("phone")} />
        </div>
      </div>
      <div>
        <label htmlFor="contact-email" className="input-label">Email address *</label>
        <input id="contact-email" data-testid="contact-form-email-input" type="email" className="input-field" value={form.email} onChange={set("email")} required />
      </div>
      <div>
        <label htmlFor="contact-location" className="input-label">Where does your loved one live?</label>
        <input
          id="contact-location"
          data-testid="contact-form-location-input"
          className="input-field"
          placeholder="e.g. At home in Varsity, assisted living in Bridgeland…"
          value={form.senior_location}
          onChange={set("senior_location")}
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="input-label">Your message *</label>
        <textarea id="contact-message" data-testid="contact-form-message-input" rows={5} className="input-field resize-none" value={form.message} onChange={set("message")} required />
      </div>
      <button type="submit" data-testid="contact-form-submit-button" disabled={submitting} className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60">
        {submitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Sending…
          </>
        ) : (
          <>
            <Send className="h-5 w-5" /> Send Message
          </>
        )}
      </button>
    </form>
  );
};

export default function Contact() {
  const [tab, setTab] = useState("book");

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-4 pb-12 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <p className="eyebrow mb-5" data-testid="contact-eyebrow">We'd Love to Hear From You</p>
        <h1 className="max-w-3xl font-serif text-4xl font-bold leading-[1.1] tracking-tight text-espresso sm:text-5xl lg:text-6xl" data-testid="contact-heading">
          <MaskedLine delay={0.1}>Let's talk about</MaskedLine>
          <MaskedLine delay={0.25}>
            your <em className="text-brand">loved one.</em>
          </MaskedLine>
        </h1>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <FadeIn delay={0.3}>
          <div className="mb-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="contact-info-cards">
            {CONTACT_CARDS.map((c) => {
              const inner = (
                <>
                  <c.icon className="mb-3 h-7 w-7 text-brand" />
                  <p className="text-sm font-bold uppercase tracking-widest text-warmgray">{c.label}</p>
                  <p className="mt-1 break-words text-lg font-semibold leading-snug text-espresso">{c.value}</p>
                </>
              );
              const cls = "block h-full rounded-2xl border border-espresso/10 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-espresso/10";
              return c.href ? (
                <a
                  key={c.label}
                  href={c.href}
                  data-testid={c.testid}
                  className={cls}
                  {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                >
                  {inner}
                </a>
              ) : (
                <div key={c.label} data-testid={c.testid} className={cls}>{inner}</div>
              );
            })}
          </div>
        </FadeIn>

        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="mb-8 flex rounded-full border border-espresso/10 bg-sand p-1.5" role="tablist" aria-label="Contact options">
              {[
                { id: "book", label: "Book a Free Consultation", testid: "contact-tab-booking" },
                { id: "message", label: "Send a Message", testid: "contact-tab-message" },
              ].map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={tab === t.id}
                  data-testid={t.testid}
                  onClick={() => setTab(t.id)}
                  className={`flex-1 rounded-full px-4 py-3 text-base font-semibold transition-all duration-300 sm:text-lg ${
                    tab === t.id ? "bg-brand text-white shadow-md shadow-brand/25" : "text-espresso hover:text-brand"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-espresso/10 bg-white p-6 shadow-xl shadow-espresso/10 sm:p-10" data-testid="contact-form-panel">
              {tab === "book" ? <BookingForm idPrefix="booking-form" /> : <ContactForm />}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
